'use client'

import { useState, useEffect, useRef } from 'react'

export interface SearchResult {
  id: string | number
  title: string
  slug: string
  excerpt: string
  collection: string
  priority: number
}

interface UseSearchOptions {
  /** Only return results from this Payload collection slug */
  collection?: string
  /** Max results to return (default 50) */
  limit?: number
  /** Debounce delay in ms (default 300) */
  debounce?: number
}

/**
 * Hook that queries the Payload `search` collection for indexed full-text search.
 *
 * When `query` is empty, `results` is `null` (meaning "show everything").
 * When a query is active, `results` is the array of matching search records.
 * `matchingIds` is a convenience Set of document IDs from the results.
 */
export function useSearch(options: UseSearchOptions = {}) {
  const { collection, limit = 50, debounce = 300 } = options

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const trimmed = query.trim()

    if (!trimmed) {
      setResults(null)
      setLoading(false)
      return
    }

    setLoading(true)

    const timer = setTimeout(async () => {
      // Abort any in-flight request
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      try {
        // Build Payload REST API query params
        const params = new URLSearchParams()
        params.set('where[title][like]', trimmed)
        params.set('limit', String(limit))
        params.set('depth', '0')
        if (collection) {
          params.set('where[doc.relationTo][equals]', collection)
        }

        const res = await fetch(`/api/search?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!res.ok) throw new Error('Search failed')
        const data = await res.json()

        const mapped: SearchResult[] = (data.docs || []).map((doc: any) => ({
          id: typeof doc.doc?.value === 'object' ? doc.doc.value.id : doc.doc?.value,
          title: doc.title || '',
          slug: doc.slug || '',
          excerpt: doc.excerpt || '',
          collection: doc.doc?.relationTo || '',
          priority: doc.priority || 0,
        }))

        setResults(mapped)
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setResults([])
        }
      } finally {
        setLoading(false)
      }
    }, debounce)

    return () => {
      clearTimeout(timer)
      abortRef.current?.abort()
    }
  }, [query, collection, limit, debounce])

  // Convenience: set of matching document IDs
  const matchingIds = results ? new Set(results.map((r) => r.id)) : null

  return { query, setQuery, results, matchingIds, loading }
}
